import React, { useState, useEffect, useContext, Fragment } from "react";
import { Input, Label, FormGroup } from "reactstrap";
import { APPConstants } from "../../../../../constants/app";
import { ProjectContext } from "../../../../../context/Project";
import ImagePreview from "./ImagePreview";
import useBlobStorage from "../../../../../hooks/useBlobStorage";
import Plus from '../../../assets/plus.svg';
import PictureIcon from '../../../assets/picture-icon.svg';
import CrossCircle from '../../../assets/cross-circle.svg';

const ImageUpload = ({onFilesUploaded}) => {
    const [selectedFiles, setSelectedFiles] = useState(undefined);
    const [selectedFilesArray, setSelectedFilesArray] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [notice, setNotice] = useState('');
    const [imageKey, setImageKey] = useState(Date.now());
    const [addedHashes, setAddedHashes] = useState([]);
    const blobStorage = useBlobStorage();

    const { getSpacePhotos, setSpacePhotos } = useContext(ProjectContext);

    useEffect(() => {
        const spacePhotos = getSpacePhotos();

        if (spacePhotos && spacePhotos.length > 0 && imagePreviews.length === 0) {
            setImagePreviews(spacePhotos);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (blobStorage.uploadedFiles?.length > 0 && imagePreviews?.length > 0) {
            let reducedImagePreviews = [...imagePreviews];
            blobStorage.uploadedFiles.forEach((uploadedFile) => {
                for (const imagePreview of reducedImagePreviews) {
                    if (!imagePreview.uploadedFile) {
                        imagePreview.uploadedFile = uploadedFile;
                        imagePreview.url = blobStorage.getUploadedFileUrl(uploadedFile);
                        break;
                    }
                }
            });
            setImagePreviews(reducedImagePreviews);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blobStorage.uploadedFiles]);

    useEffect(() => {
        if (blobStorage.error && addedHashes?.length > 0) {
            removeFilesByHash(addedHashes);
            setNotice(resolveUploadErrorNotice());
            setAddedHashes([]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blobStorage.error]);

    useEffect(() => {
        if (selectedFiles) {
            setNotice('');

            let previousHashes = [];

            imagePreviews.forEach((imagePreview) => {
                previousHashes.push(imagePreview.hash);
            });

            let newPreviews = getPreviews(selectedFiles);
            let newSelectedFiles = Array.from(selectedFiles);

            const reducedImagePreviews = newPreviews.filter((imagePreview) => {
                return !previousHashes.includes(imagePreview.hash);
            });

            const reducedSelectedFiles = newSelectedFiles.filter((selectedFile) => {
                return !previousHashes.includes(getFileHash(selectedFile));
            });

            setImagePreviews([...imagePreviews, ...reducedImagePreviews]);
            setSelectedFilesArray([...selectedFilesArray, ...reducedSelectedFiles]);
            setAddedHashes(reducedImagePreviews.map(preview => preview.hash));

            blobStorage.uploadFormFilesBlob(reducedSelectedFiles);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFiles]);

    useEffect(() => {
        setSpacePhotos(imagePreviews);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagePreviews]);

    const selectFiles = (event) => {
        const qtyValid = validateUploadedFilesQty(event.target.files);
        let sizeValid = false;

        if (qtyValid) {
            sizeValid = validateUploadedFilesSize(event.target.files);
        }

        if (!qtyValid || !sizeValid) {
            event.target.value = '';
            const message = !qtyValid 
                ? "You can upload up to " + APPConstants.UPLOAD_MAX_FILE_QTY + " photos."
                : "Max uplod size is " + APPConstants.UPLOAD_MAX_FILE_SIZE_MB + " MB.";
            
            setNotice(message);
            return;
        }

        setSelectedFiles(event.target.files);
    };

    const validateUploadedFilesSize = (files) => {
        let bytesTotal = 0;
        for (const file of files) {
            bytesTotal += file.size;
        }
        return bytesTotal <= APPConstants.UPLOAD_MAX_FILE_SIZE_MB * 1048576;
    }

    const validateUploadedFilesQty = (files) => {
        let count = 0;
        for (const file of files) {
            count++;
        }
        return count <= APPConstants.UPLOAD_MAX_FILE_QTY;
    }

    const getPreviews = (files) => {
        let images = [];

        if (files) {
            for (let i = 0; i < files.length; i++) {
                images.push(
                    {
                        blob: URL.createObjectURL(files[i]),
                        hash: getFileHash(files[i]),
                        name: files[i].name,
                        size: files[i].size
                    }
                )
            }
        }
        
        return images;
    }

    const getFileHash = (file) => {
        return file?.size + file?.name + file?.lastModified;
    }

    const removeFilesByHash = (fileHashes) => {
        if (!Array.isArray(fileHashes)) {
            return;
        }

        const reducedSelectedFilesArray = selectedFilesArray.filter((selectedFile) => {
            return !fileHashes.includes(getFileHash(selectedFile));
          });

        const reducedImagePreviews = imagePreviews.filter((imagePreview) => {
            return !fileHashes.includes(imagePreview.hash);
          });

        setImagePreviews(reducedImagePreviews);
        setSelectedFilesArray(reducedSelectedFilesArray);
        setNotice('');
    }

    const resolveUploadErrorNotice = () => {
        let errorNotice = 'An error occurred and we couldn´t upload your image(s). Please try again later.';

        if (blobStorage.error?.detail) {
            errorNotice = blobStorage.error?.detail;
        }

        return errorNotice;
    }

    const handlePreviewClick = (file) => {
        if (file?.url?.length > 0) {
            setPreviewUrl(file.url);
        }
    }

    const handleFileRemoveClick = (fileHash) => {
        removeFilesByHash([fileHash]);
    }

    const Previews = () => {
        return imagePreviews.length ?
            imagePreviews.map((file, i) =>
                <Fragment key={i}>
                    <div className="item-upload selected">
                        <div className="cont-img">
                            <a
                                className="preview-remove"
                                onClick={ e => handleFileRemoveClick(file.hash, e) }
                            >
                                <img src={CrossCircle} alt=""/>
                            </a>
                            <img 
                                src={file.url || file.blob}
                                alt={'preview-' + i}
                                style={{cursor: file?.url ? "pointer" : "auto"}}
                                onClick={(e) => handlePreviewClick(file, e)}
                                onError={(e) => {e.target.src = PictureIcon}}
                            />
                        </div>
                    </div>
                </Fragment>
            ) : (
            <div className="item-upload selected">
                <div className="cont-img">
                    <img
                        className="preview-placeholder"
                        src={PictureIcon} alt="PictureIcon"
                    />
                </div>
            </div>
            )
    }

    return (
        <section className="m-t-12 upload-box">
            <div className="upload-box-cont">
                <Previews/>
                <div className="item-upload">
                    <FormGroup className="cont-img">
                        <Input
                            id="upload-image"
                            name="upload_image"
                            type="file"
                            multiple
                            accept="image/*"
                            key={imageKey}
                            onChange={selectFiles}
                        />
                        <Label className="upload-control" for="upload-image">
                            <img src={Plus} alt="Upload file" />
                        </Label>
                    </FormGroup>
                </div>
            </div>
            {
                notice &&
                <span className="text-label"><i>{notice}</i></span>
            }
            <ImagePreview previewUrl={previewUrl} onVisibleChanged={setPreviewUrl}/>
        </section>
    );
};

export default ImageUpload;
